/**
 * Email API Service
 * Handles all email client related API calls
 */

import { getApiClient } from './apiClient';

const EMAIL_BASE = '/admin/email';

/**
 * Email folder types
 */
export const EmailFolder = {
  INBOX: 'INBOX',
  SENT: 'SENT',
  DRAFTS: 'DRAFTS',
  JUNK: 'JUNK',
  TRASH: 'TRASH',
  ARCHIVE: 'ARCHIVE',
};

/**
 * Email Accounts API
 */
export const emailAccountApi = {
  /**
   * Get all email accounts
   */
  getAll: async () => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/accounts`);
    return response.data;
  },

  /**
   * Get active email accounts only
   */
  getActive: async () => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/accounts/active`);
    return response.data;
  },

  /**
   * Get email account by ID
   */
  getById: async (id) => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/accounts/${id}`);
    return response.data;
  },

  /**
   * Create a new email account
   */
  create: async (account) => {
    const client = getApiClient();
    const response = await client.post(`${EMAIL_BASE}/accounts`, account);
    return response.data;
  },

  /**
   * Update an email account
   */
  update: async (id, account) => {
    const client = getApiClient();
    const response = await client.put(`${EMAIL_BASE}/accounts/${id}`, account);
    return response.data;
  },

  /**
   * Delete an email account
   */
  delete: async (id) => {
    const client = getApiClient();
    await client.delete(`${EMAIL_BASE}/accounts/${id}`);
  },

  /**
   * Test connection for email account settings
   */
  testConnection: async (account) => {
    const client = getApiClient();
    const response = await client.post(`${EMAIL_BASE}/accounts/test-connection`, account);
    return response.data;
  },
};

/**
 * Email Inbox API
 */
export const emailInboxApi = {
  /**
   * Get inbox emails for an account
   */
  getInbox: async (accountId, page = 1, pageSize = 20, folder = 'INBOX') => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/${accountId}/inbox`, {
      params: { page, pageSize, folder },
    });
    return response.data;
  },

  /**
   * Get a single email by UID
   */
  getEmail: async (accountId, uid, folder = 'INBOX') => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/${accountId}/email/${uid}`, {
      params: { folder },
    });
    return response.data;
  },

  /**
   * Get attachment download URL
   */
  getAttachmentUrl: (accountId, uid, index, folder = 'INBOX') => {
    const baseUrl = getApiClient().defaults.baseURL;
    return `${baseUrl}${EMAIL_BASE}/${accountId}/email/${uid}/attachment/${index}?folder=${folder}`;
  },

  /**
   * Download attachment
   */
  downloadAttachment: async (accountId, uid, index, folder = 'INBOX') => {
    const client = getApiClient();
    const response = await client.get(
      `${EMAIL_BASE}/${accountId}/email/${uid}/attachment/${index}`,
      {
        params: { folder },
        responseType: 'blob',
      }
    );
    return response;
  },
};

/**
 * Email Folder API
 */
export const emailFolderApi = {
  /**
   * Get folder counts for an account
   */
  getCounts: async (accountId) => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/${accountId}/folders/counts`);
    return response.data;
  },

  /**
   * Get emails by folder (uses cached metadata)
   */
  getByFolder: async (accountId, folder, page = 1, pageSize = 20) => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/${accountId}/folder/${folder}`, {
      params: { page, pageSize },
    });
    return response.data;
  },

  /**
   * Get starred emails
   */
  getStarred: async (accountId) => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/${accountId}/starred`);
    return response.data;
  },
};

/**
 * Email Actions API
 */
export const emailActionsApi = {
  /**
   * Move email to a folder
   */
  move: async (accountId, uid, targetFolder) => {
    const client = getApiClient();
    const response = await client.post(
      `${EMAIL_BASE}/${accountId}/email/${uid}/move`,
      null,
      { params: { folder: targetFolder } }
    );
    return response.data;
  },

  /**
   * Toggle star on email
   */
  toggleStar: async (accountId, uid, starred = true) => {
    const client = getApiClient();
    const response = await client.post(
      `${EMAIL_BASE}/${accountId}/email/${uid}/star`,
      null,
      { params: { starred } }
    );
    return response.data;
  },

  /**
   * Mark email as read/unread
   */
  markRead: async (accountId, uid, read = true) => {
    const client = getApiClient();
    const response = await client.post(
      `${EMAIL_BASE}/${accountId}/email/${uid}/read`,
      null,
      { params: { read } }
    );
    return response.data;
  },

  /**
   * Delete email (move to trash or permanent delete)
   */
  delete: async (accountId, uid, permanent = false) => {
    const client = getApiClient();
    const response = await client.delete(`${EMAIL_BASE}/${accountId}/email/${uid}`, {
      params: { permanent },
    });
    return response.data;
  },

  /**
   * Archive email
   */
  archive: async (accountId, uid) => {
    return emailActionsApi.move(accountId, uid, EmailFolder.ARCHIVE);
  },

  /**
   * Mark as junk/spam
   */
  markAsJunk: async (accountId, uid) => {
    return emailActionsApi.move(accountId, uid, EmailFolder.JUNK);
  },
};

/**
 * Email Drafts API
 */
export const emailDraftsApi = {
  /**
   * Get all drafts for an account
   */
  getAll: async (accountId, page = 1, pageSize = 20) => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/${accountId}/drafts`, {
      params: { page, pageSize },
    });
    return response.data;
  },

  /**
   * Get a specific draft
   */
  getById: async (accountId, draftId) => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/${accountId}/drafts/${draftId}`);
    return response.data;
  },

  /**
   * Create a new draft
   */
  create: async (accountId, draft) => {
    const client = getApiClient();
    const response = await client.post(`${EMAIL_BASE}/${accountId}/drafts`, draft);
    return response.data;
  },

  /**
   * Update an existing draft
   */
  update: async (accountId, draftId, draft) => {
    const client = getApiClient();
    const response = await client.put(`${EMAIL_BASE}/${accountId}/drafts/${draftId}`, draft);
    return response.data;
  },

  /**
   * Delete a draft
   */
  delete: async (accountId, draftId) => {
    const client = getApiClient();
    await client.delete(`${EMAIL_BASE}/${accountId}/drafts/${draftId}`);
  },
};

/**
 * Email Sync API
 */
export const emailSyncApi = {
  /**
   * Sync a specific account
   */
  syncAccount: async (accountId) => {
    const client = getApiClient();
    const response = await client.post(`${EMAIL_BASE}/${accountId}/sync`);
    return response.data;
  },

  /**
   * Sync all accounts
   */
  syncAll: async () => {
    const client = getApiClient();
    const response = await client.post(`${EMAIL_BASE}/sync-all`);
    return response.data;
  },

  /**
   * List available IMAP folders for debugging
   */
  listImapFolders: async (accountId) => {
    const client = getApiClient();
    const response = await client.get(`${EMAIL_BASE}/${accountId}/imap-folders`);
    return response.data;
  },
};

/**
 * Email Send API
 */
export const emailSendApi = {
  /**
   * Send email with attachments (multipart form)
   */
  send: async (accountId, emailData, attachments = []) => {
    const client = getApiClient();
    const formData = new FormData();

    formData.append('to', emailData.to.join(','));
    if (emailData.cc && emailData.cc.length > 0) {
      formData.append('cc', emailData.cc.join(','));
    }
    formData.append('subject', emailData.subject || '');
    formData.append('body', emailData.body || '');
    formData.append('isHtml', emailData.isHtml || false);

    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    const response = await client.post(`${EMAIL_BASE}/${accountId}/send`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Send email without attachments (JSON)
   */
  sendJson: async (accountId, emailData) => {
    const client = getApiClient();
    const response = await client.post(`${EMAIL_BASE}/${accountId}/send-json`, emailData);
    return response.data;
  },
};

const emailApi = {
  accounts: emailAccountApi,
  inbox: emailInboxApi,
  folders: emailFolderApi,
  actions: emailActionsApi,
  drafts: emailDraftsApi,
  sync: emailSyncApi,
  send: emailSendApi,
  EmailFolder,
};

export default emailApi;




